<template>
  <div data-testid="reporting-unit-table">
    <div v-if="loading" data-testid="reporting-unit-table__loading-skeleton">
      <div v-for="n in 10" :key="n" class="d-flex align-center pa-4 border-bottom" data-testid="reporting-unit-table__skeleton-row">
        <v-skeleton-loader data-testid="reporting-unit-table__skeleton-code" type="text" width="40" height="20" class="mr-4"></v-skeleton-loader>
        <v-skeleton-loader data-testid="reporting-unit-table__skeleton-name" type="text" width="120" height="20" class="mr-4"></v-skeleton-loader>
        <v-skeleton-loader data-testid="reporting-unit-table__skeleton-desc" type="text" width="180" height="20" class="mr-4 flex-grow-1"></v-skeleton-loader>
       
      </div>
    </div>
    <lb-datatablenew
      data-testid="reporting-unit-table__datatable"
      :headers="headers"
      :items="clickableItems"
      :enableslot="['indexno', 'code','type','action', 'usersflag', 'materialityflag_actual', 'materialityflag_forecast','sod_status', 'temporary', 'role']"
      :loading="loading"
      v-else-if="items.length > 0"
      @loadNextPage="$emit('loadNextPage', $event)"
      :currentPage="page"
      :metaData="meta"
      :filterheaders="filterOptions"
      @loadFilterData="$emit('loadFilterData', $event)"
      :hide-default-footer="true"
    >
      <template v-slot:indexno="{ item }">
        <div class="text-center" data-testid="reporting-unit-table__sno">
          {{ getSerialNumber(item) }}
        </div>
      </template>
      <template v-slot:code="{ item }">
        <div data-testid="reporting-unit-table__code-name">
          {{ item.code }} - <span>{{ item.name }}</span>
        </div>
      </template>
      <template v-slot:type="{ item }">
        <div data-testid="reporting-unit-table__type-name" class="text-uppercase">
          {{ item.type }}  
        </div>
      </template>
      <template v-slot:usersflag="{ item }">
        <StatusIndicator
          data-testid="reporting-unit-table__users-flag-status"
          :variant="item.usersflag ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
          :customLabel="item.usersflag ? 'Yes' : 'No'"
          size="sm"
        />
      </template>
      <template v-slot:materialityflag_actual="{ item }">
        <div class="d-flex justify-center">
          <v-skeleton-loader
            v-if="materialityLoading && item.materialityflag_actual === null"
            data-testid="reporting-unit-table__materiality-actual-loader"
            type="text"
            width="40"
            height="20"
          />
          <StatusIndicator
            v-else
            data-testid="reporting-unit-table__materiality-actual-status"
            :variant="item.materialityset_actual ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
            :customLabel="item.materialityset_actual ? 'Yes' : 'No'"
            size="sm"
          />
        </div>
      </template>
      <template v-slot:materialityflag_forecast="{ item }">
        <div class="d-flex justify-center">
          <v-skeleton-loader
            v-if="materialityLoading && item.materialityflag_forecast === null"
            data-testid="reporting-unit-table__materiality-forecast-loader"
            type="text"
            width="40"
            height="20"
          />
          <StatusIndicator
            v-else
            data-testid="reporting-unit-table__materiality-forecast-status"
            :variant="item.materialityset_forecast ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
            :customLabel="item.materialityset_forecast ? 'Yes' : 'No'"
            size="sm"
          />
        </div>
      </template>
      <template v-slot:sod_status="{ item }">
        <div class="d-flex justify-center">
          <StatusIndicator
            data-testid="reporting-unit-table__sod-status"
            :variant="item.sodEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
            :customLabel="item.sod_status"
            size="sm"
          />
        </div>
      </template>
      <template v-slot:action="{ item }">
        <div class="d-flex align-center justify-center" data-testid="reporting-unit-table__action-cell">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon 
                v-bind="attrs" 
                v-on="on"
                @click.stop="ViewDetails(item)"
                data-testid="reporting-unit-table__view-details-btn"
              >
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>View Details</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:role="{ item }">
        <div class="d-flex align-center" data-testid="reporting-unit-table__role-cell">
          <span data-testid="reporting-unit-table__role-local-approver" v-if="item.approver_list.indexOf(selectedUserId) > -1">{{
            $nova.labelsName('my_reporting_units_page', 'commentary_local_approver_label','Local Approver')
            }}</span>
          <span data-testid="reporting-unit-table__role-local-preparer" v-else-if="item.preparer_list.indexOf(selectedUserId) > -1">{{
            $nova.labelsName('my_reporting_units_page', 'commentary_local_preparer_label','Local Preparer')
            }}</span>
          <span data-testid="reporting-unit-table__role-regional-approver" v-else-if="item.regional_approver_list.indexOf(selectedUserId) > -1">{{
            $nova.labelsName('my_reporting_units_page', 'commentary_regional_approver_label','Regional Approver')
            }}</span>
          <span data-testid="reporting-unit-table__role-manager" v-else-if="item.manager_list.indexOf(selectedUserId) > -1">Manager</span>
          <span data-testid="reporting-unit-table__role-view-only" v-else>View only</span>
        </div>
      </template>
    </lb-datatablenew>

    <!-- Empty State -->
    <div v-else-if="!loading && items.length === 0" class="d-flex justify-center align-center" data-testid="reporting-unit-table__empty-state-container">
      <NoDataState
        :title="UI_CONSTANTS.FILTER_NO_DATA_TITLE"
        :description="UI_CONSTANTS.FILTER_NO_DATA_DESCRIPTION"
        :icon="UI_CONSTANTS.NO_DATA_ICON"
        :iconSize="UI_CONSTANTS.NO_DATA_ICON_SIZE"
        iconColor="grey"
      />
    </div>

    <!-- Pagination Controls -->
    <!-- Pagination wrapper - positioned outside scrollable content -->
    <div class="ru-list-pagination-wrapper" v-if="totalPages > 1">
      <div 
        v-if="loading"
        data-testid="reporting-unit-table__pagination-skeleton"
        style="bottom: 0; overflow: hidden; background: white;"
      >
        <v-divider></v-divider>
        <v-row justify="center" class="d-flex pa-0 ma-0 align-center justify-space-between">
          <v-col cols="6" class="pa-0">
            <v-container class="pa-0 ma-0 d-flex">
              <v-skeleton-loader
                type="text"
                width="300"
                height="32"
                class="mx-auto"
              ></v-skeleton-loader>
            </v-container>
          </v-col>
          <v-col cols="3">
            <div class="d-flex align-center justify-end">
              <v-skeleton-loader
                type="text"
                width="150"
                height="32"
              ></v-skeleton-loader>
            </div>
          </v-col>
        </v-row>
      </div>
      <Pagination
        v-else
        v-model="internalPage"
        :list="[]"
        :length-override="totalPages"
        :itemsperpage="perpage"
        :hide-on-single-page="false"
        @paginated-data="handlePaginationData"
      />
    </div>
  </div>
</template>

<script>
import StatusIndicator from '../../../components/StatusIndicator.vue';
import NoDataState from '../../../components/NoDataState.vue';
import { STATUS_VARIANTS } from '../../../utils/statusConstants';
import { VARIANCE_ANALYSIS_UI } from '../../../utils/constants';
import Pagination from '@/views/common/Pagination.vue'; // Correct relative or alias path

export default {
  name: 'ReportingUnitTable',
  components: {
    StatusIndicator,
    NoDataState,
    Pagination,
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    perpage: {
      type: Number,
      default: 50,
    },
    filterOptions: {
      type: Array,
      default: () => [],
    },
    materialityLoading: {
      type: Boolean,
      default: false,
    },
    selectedUserId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      STATUS_VARIANTS,
      UI_CONSTANTS: VARIANCE_ANALYSIS_UI,
      internalPage: this.page || 1,
    };
  },
  watch: {
    page(newVal) {
      if (newVal !== this.internalPage) {
        this.internalPage = newVal;
      }
    }
  },
  computed: {
    /**
     * Calculate total number of pages based on meta totalCount and perpage
     */
    totalPages() {
      if (!this.meta?.totalCount) return 1;
      return Math.ceil(this.meta.totalCount / this.perpage);
    },
    clickableItems() {
       const items = (this.items || []).map(item => ({
        ...item,
        __click: `/varianceanalysis/reportingunit/reportingdetails/${item._id}`
       }));
       return items;
    },
  },
  methods: {
    getSerialNumber(item) {
      const offset = (Number(this.internalPage || this.page || 1) - 1) * (Number(this.perpage) || 25);
      const rowIndex = this.clickableItems.indexOf(item);
      if (rowIndex >= 0) return offset + rowIndex + 1;
      if (item && (typeof item.indexno === 'number' || typeof item.indexno === 'string')) return item.indexno;
      return offset + 1;
    },
    ViewDetails(item) {
      if (item.__click) {
        this.$nova.gotoLink({ path: item.__click });
      } else {
        // Fallback if not configured
        this.$emit('view-details', item._id);
      }
    },
    // Handler for pagination component events
    handlePaginationData(payload) {
      // The Pagination component emits the current page in the payload as 'page'
      // Or we can rely on internalPage updates via v-model
      const targetPage = payload.page || this.internalPage;
      this.handlePageChange(targetPage);
    },
    /**
     * Handle page change from pagination or go-to input
     * @param {number} newPage - The page number to navigate to
     */
    handlePageChange(newPage) {
      const pageValue = parseInt(newPage, 10);
      let targetPage = pageValue;

      // Validate page bounds
      if (pageValue > this.totalPages) {
        targetPage = this.totalPages;
      } else if (pageValue < 1) {
        targetPage = 1;
      }

      if (targetPage !== this.page) {
        this.$emit('update:page', targetPage);
        this.$emit('loadNextPage', { page: targetPage });
      }
    },
    /**
     * Navigate to previous page and emit event to parent
     */
    goToPreviousPage() {
      if (this.page > 1) {
        this.handlePageChange(this.page - 1);
      }
    },
    /**
     * Navigate to next page and emit event to parent
     */
    goToNextPage() {
      if (this.page < this.totalPages) {
        this.handlePageChange(this.page + 1);
      }
    },
    /**
     * Change to a specific page
     * @param {number} newPage - The page number to navigate to
     */
    changePage(newPage) {
      this.handlePageChange(newPage);
    },
  },
};
</script>
<style lang="css" scoped>
</style>
